<template>
	<div>
		<div class="flex items-center mb-4 justify-center">
			<h3 class="mb-0 mr-1">{{ golfCourse.name }}:</h3>
			<course-tee-title :tee-hex="courseTee.hex" :tee-name="courseTee.tee"></course-tee-title>
		</div>

		<div class="flex justify-center mb-4 text-lg">
			<golf-score-graphic v-if="golfRound" :golf-round="golfRound"></golf-score-graphic>
		</div>

		<table class="round-snapshot-container w-full mb-4 mt-4">
			<tr class="scorecard-row">
				<td class="scorecard-heading font-bold">Hole</td>
				<td
					class="scorecard-hole"
					:key="`front-hole-number-${uuid}`"
					v-for="{ hole, uuid } in frontNine"
				>
					{{ hole }}
				</td>
				<td class="scorecard-heading font-bold">Out</td>
				<td class="scorecard-heading">&nbsp;</td>
				<td class="scorecard-heading">&nbsp;</td>
			</tr>
			<tr v-if="!roundMode" class="scorecard-row">
				<td class="scorecard-heading font-bold">Yards</td>
				<td
					class="scorecard-hole font-semibold"
					:key="`front-yards-${uuid}`"
					v-for="{ yards, uuid } in frontNine"
				>
					{{ yards }}
				</td>
				<td class="scorecard-heading font-bold">{{ front_nine_yards }}</td>
				<td class="scorecard-heading">&nbsp;</td>
				<td class="scorecard-heading">&nbsp;</td>
			</tr>
			<tr class="scorecard-row">
				<td class="scorecard-heading font-bold">Par</td>
				<td
					class="scorecard-hole font-semibold"
					:key="`front-par-${uuid}`"
					v-for="{ par, uuid } in frontNine"
				>
					{{ par }}
				</td>
				<td class="scorecard-heading font-bold">{{ front_nine_par }}</td>
				<td class="scorecard-heading">&nbsp;</td>
				<td class="scorecard-heading">&nbsp;</td>
			</tr>
			<tr v-if="!roundMode" class="scorecard-row">
				<td class="scorecard-heading font-bold">S/I</td>
				<td
					class="scorecard-hole font-semibold"
					:key="`front-si-${uuid}`"
					v-for="{ stroke_index, uuid } in frontNine"
				>
					{{ stroke_index }}
				</td>
				<td class="scorecard-heading font-bold">&nbsp;</td>
				<td class="scorecard-heading">&nbsp;</td>
				<td class="scorecard-heading">&nbsp;</td>
			</tr>
			<tr v-if="roundMode" class="scorecard-row">
				<td class="scorecard-heading"></td>
				<td
					class="scorecard-hole font-semibold"
					:class="scoreClasses(hole)"
					:key="`front-score-${hole.uuid}`"
					v-for="hole in frontNine"
				>
					{{ hole.score }}
				</td>
				<td class="scorecard-heading font-bold">{{ golfRound.front_nine_score }}</td>
				<td class="scorecard-heading">&nbsp;</td>
				<td class="scorecard-heading">&nbsp;</td>
			</tr>
			<tr height="2" style="border-top:1px solid; border-bottom:1px solid;">
				<td style="padding:0" colspan="13"></td>
			</tr>
			<tr class="scorecard-row">
				<td class="scorecard-heading font-bold">Hole</td>
				<td
					class="scorecard-hole font-semibold"
					:key="`back-hole-number-${uuid}`"
					v-for="{ hole, uuid } in backNine"
				>
					{{ hole }}
				</td>
				<td class="scorecard-heading font-bold">In</td>
				<td class="scorecard-heading font-bold">Total</td>
				<td class="scorecard-heading font-bold">To Par</td>
			</tr>
			<tr v-if="!roundMode" class="scorecard-row">
				<td class="scorecard-heading font-bold">Yards</td>
				<td
					class="scorecard-hole font-semibold"
					:key="`back-yards-${uuid}`"
					v-for="{ yards, uuid } in backNine"
				>
					{{ yards }}
				</td>
				<td class="scorecard-heading font-bold">{{ back_nine_yards }}</td>
				<td class="scorecard-heading font-bold">{{ total_yards }}</td>
				<td class="scorecard-heading">&nbsp;</td>
			</tr>
			<tr class="scorecard-row">
				<td class="scorecard-heading font-bold">Par</td>
				<td
					class="scorecard-hole font-semibold"
					:key="`back-par-${hole.uuid}`"
					v-for="hole in backNine"
				>
					{{ hole.par }}
				</td>
				<td class="scorecard-heading font-bold">{{ back_nine_par }}</td>
				<td class="scorecard-heading font-bold">{{ par }}</td>
				<td class="scorecard-heading">&nbsp;</td>
			</tr>
			<tr v-if="!roundMode" class="scorecard-row">
				<td class="scorecard-heading font-bold">S/I</td>
				<td
					class="scorecard-hole font-semibold"
					:key="`back-si-${hole.uuid}`"
					v-for="hole in backNine"
				>
					{{ hole.stroke_index }}
				</td>
				<td class="scorecard-heading font-bold">&nbsp;</td>
				<td class="scorecard-heading font-bold">&nbsp;</td>
				<td class="scorecard-heading">&nbsp;</td>
			</tr>
			<tr v-if="roundMode" class="scorecard-row">
				<td class="scorecard-heading"></td>
				<td
					class="scorecard-hole"
					:class="scoreClasses(hole)"
					:key="`back-score-${hole.uuid}`"
					v-for="hole in backNine"
				>
					{{ hole.score }}
				</td>
				<td class="scorecard-heading font-bold">{{ golfRound.back_nine_score }}</td>
				<td class="scorecard-heading font-bold">{{ golfRound.gross_score }}</td>
				<td
					class="scorecard-heading font-bold"
					:class="{
						'bg-red-600 text-white ': golfRound.to_par < 0,
						'bg-black text-white ': golfRound.to_par > 0
					}"
				>
					{{ golfRound.current_score }}
				</td>
			</tr>
		</table>

		<div class="flex" v-if="roundMode">
			<round-hole-edit
				class="mb-4 w-full"
				v-model="scorecardHoles[editingHole - 1]"
				:golf-round="golfRound"
				@update-score="updateScore"
				@go-to-previous-hole="gotoPreviousHole"
				@go-to-next-hole="gotoNextHole"
				@save-round="saveRound"
			/>
		</div>
		<div class="flex">
			<course-hole-edit
				v-if="!roundMode"
				class="mr-2 w-1/4"
				v-model="scorecardHoles[editingHole - 1]"
				@update-yards="updateYards"
				@update-par="updatePar"
				@go-to-previous-hole="gotoPreviousHole"
				@go-to-next-hole="gotoNextHole"
			/>
			<div v-if="roundMode" class="bg-white w-full p-4">
				<h3 class="m-0 text-center font-bold">Quick Stats</h3>
				<table class="table text-xs w-full mgs-round-summary-table">
					<tr>
						<th></th>
						<th>Front 9</th>
						<th>Back 9</th>
						<th>Total</th>
						<th>%</th>
					</tr>
					<tr>
						<th>Fairways</th>
						<td>{{ fairways.front.success }}/{{ fairways.front.chances }}</td>
						<td>{{ fairways.back.success }}/{{ fairways.back.chances }}</td>
						<td>{{ fairways.total.success }}/{{ fairways.total.chances }}</td>
						<td>{{ fairways.total.percentage }}%</td>
					</tr>
					<tr>
						<th>GIR</th>
						<td>{{ gir.front.success }}/{{ gir.front.chances }}</td>
						<td>{{ gir.back.success }}/{{ gir.back.chances }}</td>
						<td>{{ gir.total.success }}/{{ gir.total.chances }}</td>
						<td>{{ gir.total.percentage }}%</td>
					</tr>
					<tr>
						<th>Putts</th>
						<td>{{ frontNinePutts }}</td>
						<td>{{ backNinePutts }}</td>
						<td>{{ totalPutts }}</td>
						<td></td>
					</tr>
					<tr>
						<th>Scrambles</th>
						<td>{{ scrambles.front.success }}/{{ scrambles.front.chances }}</td>
						<td>{{ scrambles.back.success }}/{{ scrambles.back.chances }}</td>
						<td>{{ scrambles.total.success }}/{{ scrambles.total.chances }}</td>
						<td>{{ scrambles.total.percentage }}%</td>
					</tr>
					<tr>
						<th>Penalties</th>
						<td>{{ frontNinePenalties }}</td>
						<td>{{ backNinePenalties }}</td>
						<td>{{ totalPenalties }}</td>
						<td></td>
					</tr>
				</table>
			</div>
		</div>

		<button
			v-if="roundMode"
			@click="saveRound"
			style="position:fixed!important"
			class="button mgs-button is-link fixed bottom-0 right-0"
		>
			SAVE
		</button>
	</div>
</template>
<script>
/** @format */
import holeMod from "./../../modules/hole";

export default {
	name: "CourseScorecard",
	props: {
		courseTee: {
			type: Object,
			required: true
		},
		scorecardHoles: {
			type: Array,
			required: true
		},
		roundMode: {
			type: Boolean,
			default: true
		},
		golfCourse: {
			type: Object,
			required: true
		},
		value: {
			type: Object
		}
	},
	components: {
		"round-hole-edit": () =>
			import(/* webpackChunkName: "RoundHoleEdit" */ "./../Round/HoleEdit"),
		"course-hole-edit": () =>
			import(/* webpackChunkName: "CourseHoleEdit" */ "./../Course/HoleEdit"),
		"course-tee-title": () => import(/* webpackChunkName: "CourseTeeTitle" */ "./TeeTitle"),
		"course-fairway-select": () =>
			import(/* webpackChunkName: "CourseFairwaySelect" */ "./FairwaySelect"),
		"course-scramble-select": () =>
			import(/* webpackChunkName: "CourseScrambleSelect" */ "./ScrambleSelect"),
		"golf-score-graphic": () =>
			import(/* webpackChunkName: "GolfScoreGraphic" */ "./../Round/ScoreGraphic")
	},
	methods: {
		gotoPreviousHole() {
			if (this.editingHole == 1) {
				this.editingHole = 18;
			} else {
				this.editingHole--;
			}
		},
		gotoNextHole() {
			if (this.editingHole == 18) {
				this.editingHole = 1;
			} else {
				this.editingHole++;
			}
		},
		scoreClasses(hole) {
			return holeMod.scoreClasses(hole);
		},
		saveRound() {
			const payload = {
				roundUuid: this.golfRound.uuid,
				payload: this.golfRound
			}
			this.$store
				.dispatch(`rounds/edit`, payload)
				.then(response => {
					this.$store.dispatch("notification/add", {
						type: "success",
						message: "Round saved successfully."
					});
					this.$emit("round-saved", response.data.data);
				})
				.finally(() => {
					//do stuff
				});
		},
		onInputFocus(hole, val) {
			// if (hole[val] == 0 || !hole[val]) {
			// 	hole[val] = '';
			// }
			// return hole;
		},
		reduceValues(prev, next) {
			const nextVal = !next ? 0 : next.length == 0 ? 0 : parseInt(next);
			return prev + parseInt(nextVal);
		},
		updateYards() {
			const total = this.scorecardHoles.reduce(
				(prev, next) => this.reduceValues(prev, next.yards),
				0
			);
			this.total_yards = total;

			const frontTotal = this.frontNine.reduce(
				(prev, next) => this.reduceValues(prev, next.yards),
				0
			);
			this.front_nine_yards = frontTotal;

			const backTotal = this.backNine.reduce(
				(prev, next) => this.reduceValues(prev, next.yards),
				0
			);
			this.back_nine_yards = backTotal;
			this.$nextTick(() => {
				this.$emit("tee-updated", this.courseTee);
			});
		},
		updatePar() {
			const total = this.scorecardHoles.reduce(
				(prev, next) => this.reduceValues(prev, next.par),
				0
			);
			this.par = total;

			const frontTotal = this.frontNine.reduce(
				(prev, next) => this.reduceValues(prev, next.par),
				0
			);
			this.front_nine_par = frontTotal;

			const backTotal = this.backNine.reduce(
				(prev, next) => this.reduceValues(prev, next.par),
				0
			);
			this.back_nine_par = backTotal;
			this.$nextTick(() => {
				this.$emit("tee-updated", this.courseTee);
			});
		},
		updateScore(hole) {
			hole.score = parseInt(hole.score);
			hole.par = parseInt(hole.par);
			if (Array.isArray(hole.shots) && hole.shots.length >  0) {
				const putts = hole.shots.filter(shot => shot.shot_type === 6);
				console.log(putts, 'putts')
				console.log(putts.length, 'putts.length')
				console.log(hole.shots, 'hole.shots')
				console.log('------')
				hole.putts = putts.length;

				//TODO - USE THIS WHEN WE CAN FIND AN INTUITIVE WAY OF APPLYING
				// const penalties = hole.shots.filter(shot => shot.shot_type === 7);
				// hole.penalties = penalties.length;
			} else {
				hole.putts = parseInt(hole.putts);
			}
			
			const difference = hole.score - hole.par;

			const targetShots = hole.par - 2;

			hole.gir = hole.score < 1 ? false : hole.score - hole.putts <= targetShots;
			hole.difference = difference;
			hole.score_albatross = difference == -3;
			hole.score_eagle = difference == -2;
			hole.score_birdie = difference == -1;
			hole.score_par = difference == 0;
			hole.score_bogey = difference == 1;
			hole.score_double_bogey = difference == 2;
			hole.score_triple_or_worse = difference > 2;

			const total = this.scorecardHoles.reduce(
				(prev, next) => this.reduceValues(prev, next.score),
				0
			);
			this.golfRound.gross_score = total;

			const frontTotal = this.frontNine.reduce(
				(prev, next) => this.reduceValues(prev, next.score),
				0
			);
			this.golfRound.front_nine_score = frontTotal;

			const backTotal = this.backNine.reduce(
				(prev, next) => this.reduceValues(prev, next.score),
				0
			);
			this.golfRound.back_nine_score = backTotal;

			const parSoFar = this.scorecardHoles
				.filter(h => parseInt(h.score) > 0)
				.reduce((prev, next) => this.reduceValues(prev, next.par), 0);

			this.golfRound.to_par = this.golfRound.gross_score - parSoFar;

			if (this.golfRound.to_par > 0) {
				this.golfRound.current_score = `+${this.golfRound.to_par}`;
			} else if (this.golfRound.to_par < 0) {
				this.golfRound.current_score = this.golfRound.to_par;
			} else {
				this.golfRound.current_score = "L";
			}
		}
	},
	watch: {
		golfRound: {
			deep: true,
			handler(v) {
				this.$emit("input", v);
			}
		}
	},
	computed: {
		roundIsComplete() {
			return this.scorecardHoles.filter(h => h.score > 0).length == 18;
		},
		frontNine() {
			return this.scorecardHoles.filter(h => h.hole <= 9);
		},
		backNine() {
			return this.scorecardHoles.filter(h => h.hole >= 10 && h.hole <= 18);
		},
		frontNinePutts() {
			return this.frontNine.reduce((prev, next) => this.reduceValues(prev, next.putts), 0);
		},
		backNinePutts() {
			return this.backNine.reduce((prev, next) => this.reduceValues(prev, next.putts), 0);
		},
		totalPutts() {
			return this.scorecardHoles.reduce(
				(prev, next) => this.reduceValues(prev, next.putts),
				0
			);
		},
		frontNinePenalties() {
			return this.frontNine.reduce(
				(prev, next) => this.reduceValues(prev, next.penalties),
				0
			);
		},
		backNinePenalties() {
			return this.backNine.reduce((prev, next) => this.reduceValues(prev, next.penalties), 0);
		},
		totalPenalties() {
			return this.scorecardHoles.reduce(
				(prev, next) => this.reduceValues(prev, next.penalties),
				0
			);
		},
		fairways() {
			return this.totals.fairways;
		},
		gir() {
			return this.totals.gir;
		},
		scrambles() {
			return this.totals.scrambles;
		},
		totals() {
			const totals = {
				fairways: {
					front: {
						chances: 0,
						success: 0,
						misses: 0,
						percentage: 0
					},
					back: {
						chances: 0,
						success: 0,
						misses: 0,
						percentage: 0
					},
					total: {
						chances: 0,
						success: 0,
						misses: 0,
						percentage: 0
					}
				},
				gir: {
					front: {
						chances: 0,
						success: 0,
						percentage: 0
					},
					back: {
						chances: 0,
						success: 0,
						percentage: 0
					},
					total: {
						chances: 0,
						success: 0,
						percentage: 0
					}
				},
				scrambles: {
					front: {
						chances: 0,
						success: 0,
						misses: 0,
						percentage: 0
					},
					back: {
						chances: 0,
						success: 0,
						misses: 0,
						percentage: 0
					},
					total: {
						chances: 0,
						success: 0,
						misses: 0,
						percentage: 0
					}
				}
			};

			for (let hole of this.frontNine) {
				let score = parseInt(hole.score);
				let par = parseInt(hole.par);
				if (score > 0) {
					if (par > 3) {
						totals.fairways.front.chances++;
						if (hole.fairway === true) {
							totals.fairways.front.success++;
						} else {
							totals.fairways.front.misses++;
						}
					}

					totals.gir.front.chances++;
					if (!!hole.gir) {
						totals.gir.front.success++;
					} else {
						totals.scrambles.front.chances++;
						if (score <= par) {
							totals.scrambles.front.success++;
						} else {
							totals.scrambles.front.misses++;
						}
					}
				}
			}
			totals.fairways.front.percentage = (
				(totals.fairways.front.success / totals.fairways.front.chances) *
				100
			).toFixed(2);
			totals.gir.front.percentage = (
				(totals.gir.front.success / totals.gir.front.chances) *
				100
			).toFixed(2);
			totals.scrambles.front.percentage = (
				(totals.scrambles.front.success / totals.scrambles.front.chances) *
				100
			).toFixed(2);

			for (let hole of this.backNine) {
				let score = parseInt(hole.score);
				let par = parseInt(hole.par);
				if (score > 0) {
					if (par > 3) {
						totals.fairways.back.chances++;
						if (hole.fairway === true) {
							totals.fairways.back.success++;
						} else {
							totals.fairways.back.misses++;
						}
					}
					totals.gir.back.chances++;
					if (!!hole.gir) {
						totals.gir.back.success++;
					} else {
						totals.scrambles.back.chances++;
						if (score <= par) {
							totals.scrambles.back.success++;
						} else {
							totals.scrambles.back.misses++;
						}
					}
				}
			}
			totals.fairways.back.percentage = (
				(totals.fairways.back.success / totals.fairways.back.chances) *
				100
			).toFixed(2);
			totals.fairways.total.chances =
				totals.fairways.front.chances + totals.fairways.back.chances;
			totals.fairways.total.success =
				totals.fairways.front.success + totals.fairways.back.success;
			totals.fairways.total.misses =
				totals.fairways.front.misses + totals.fairways.back.misses;
			totals.fairways.total.percentage = (
				(totals.fairways.total.success / totals.fairways.total.chances) *
				100
			).toFixed(2);

			totals.gir.back.percentage = (
				(totals.gir.back.success / totals.gir.back.chances) *
				100
			).toFixed(2);
			totals.gir.total.chances = totals.gir.front.chances + totals.gir.back.chances;
			totals.gir.total.success = totals.gir.front.success + totals.gir.back.success;
			totals.gir.total.percentage = (
				(totals.gir.total.success / totals.gir.total.chances) *
				100
			).toFixed(2);

			totals.scrambles.back.percentage = (
				(totals.scrambles.back.success / totals.scrambles.back.chances) *
				100
			).toFixed(2);
			totals.scrambles.total.chances =
				totals.scrambles.front.chances + totals.scrambles.back.chances;
			totals.scrambles.total.success =
				totals.scrambles.front.success + totals.scrambles.back.success;
			totals.scrambles.total.misses =
				totals.scrambles.front.misses + totals.scrambles.back.misses;
			totals.scrambles.total.percentage = (
				(totals.scrambles.total.success / totals.scrambles.total.chances) *
				100
			).toFixed(2);

			return totals;
		}
	},
	data() {
		return {
			par: this.courseTee.par,
			front_nine_par: this.courseTee.front_nine_par,
			back_nine_par: this.courseTee.back_nine_par,
			front_nine_yards: this.courseTee.front_nine_yards,
			back_nine_yards: this.courseTee.back_nine_yards,
			total_yards: this.courseTee.total_yards,
			front_nine_score: this.courseTee.front_nine_score,
			back_nine_score: this.courseTee.back_nine_score,
			golfRound: this.value,
			viewMode: "simple",
			editingHole: 1
		};
	}
};
</script>
<style lang="scss">
/** @format */

.front9,
.back9 {
	margin-bottom: 1em;
	max-width: 620px;
	margin-left: auto;
	margin-right: auto;
	border: 1px solid $forestGreen;
	.hole {
		background-color: $forestGreen;
		color: #fff;
		span {
			padding: 0.5em 0;
		}
	}
	.par {
		background-color: #ccc;
		color: #777;
		span {
			padding: 0.25em 0;
		}
	}
	.score {
		color: $forestGreen;
		span {
			padding: 0.25em 0;
			position: relative;
		}
		border-bottom: 1px solid $forestGreen;
		&:last-child {
			border-bottom: none;
		}
	}
	span {
		border: rgba(0, 0, 0, 0.25) solid 1px;
		border-width: 0 0 0 1px;
		display: inline-block;
		text-align: center;
		width: 3.5em;
		font-size: 14px;
		&:first-child {
			text-align: left;
			padding-left: 1em;
			border-width: 0;
			width: 75px;
		}
		&.sub-span {
		}
	}
}

@media screen and (min-width: 80em) {
	.front9,
	.back9 {
		display: inline-block;
	}
	.back9 span:first-child {
		display: none;
	}
}
.mgs-scorecard-input {
	@apply text-center;
	background: transparent;
}
.fairway-input {
	transition: all 0.3s ease;
	&:focus {
	}
}
.fairway-input + div {
	transition: all 0.3s ease;
	display: none;
	position: absolute;
	top: 0px;
	left: 0px;
	right: 0px;
	bottom: 0px;
}
.fairway-input:focus + div {
	display: flex;
	transform: scale(2);
}
.round-snapshot-container {
	background: #fffaec;
	border-radius: 4px !important;
	font-size: 12px;
	th,
	tr,
	td {
		text-align: center !important;
		border: 1px solid black;
		@apply p-2;
	}
	.scorecard-hole {
		// width:34px;
	}
	// div{
	// 	@apply flex-grow text-center;
	// }
	// .scorecard-row {

	// }
	// .scorecard-heading {

	// }
}
.mgs-round-summary-table {
	tr {
		th:last-child,
		td:last-child {
			text-align: center;
			width: 50px;
		}
		th:nth-child(2),
		th:nth-child(3),
		th:nth-child(4),
		td:nth-child(2),
		td:nth-child(3),
		td:nth-child(4) {
			text-align: center;
			width: 65px;
		}
	}
}
</style>
